import React, { useState, useEffect } from 'react';

/**
 * Overlay d'optimisation moderne
 */
export default function OptimizationOverlay({ isOptimizing, elapsedTime, showSuccess, errorMessage }) {
  const [isVisible, setIsVisible] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [successProgress, setSuccessProgress] = useState(100);
  const [errorProgress, setErrorProgress] = useState(100);

  useEffect(() => {
    if (isOptimizing) {
      // Entrée : afficher immédiatement
      setShouldRender(true);
      // Petit délai pour permettre au DOM de se monter avant l'animation
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsVisible(true);
        });
      });
      setShowSuccessMessage(false);
      setShowErrorMessage(false);
    } else if (shouldRender) {
      // Sortie : attendre la fin de l'animation avant de démonter
      setIsVisible(false);
      const timer = setTimeout(() => {
        setShouldRender(false);
        // Afficher le message de succès si demandé
        if (showSuccess) {
          setShowSuccessMessage(true);
          setSuccessProgress(100);
          setTimeout(() => setShowSuccessMessage(false), 3000);
        }
      }, 400);
      return () => clearTimeout(timer);
    }
  }, [isOptimizing, shouldRender, showSuccess]);

  // Gestion du message d'erreur
  useEffect(() => {
    if (errorMessage) {
      setShowErrorMessage(true);
      setErrorProgress(100);
      
      // Timer pour fermer automatiquement après 5 secondes
      const timer = setTimeout(() => {
        setShowErrorMessage(false);
      }, 5000);
      
      return () => clearTimeout(timer);
    } else {
      setShowErrorMessage(false);
    }
  }, [errorMessage?.timestamp]);

  // Gestion de la barre de progression du message de succès
  useEffect(() => {
    if (showSuccessMessage) {
      const duration = 3000; // 3 secondes
      const intervalTime = 30; // Mise à jour toutes les 30ms
      const decrement = (intervalTime / duration) * 100;
      
      const interval = setInterval(() => {
        setSuccessProgress((prev) => {
          const newProgress = prev - decrement;
          return newProgress > 0 ? newProgress : 0;
        });
      }, intervalTime);
      
      return () => clearInterval(interval);
    }
  }, [showSuccessMessage]);

  // Gestion de la barre de progression du message d'erreur
  useEffect(() => {
    if (showErrorMessage) {
      const duration = 5000; // 5 secondes
      const intervalTime = 30; // Mise à jour toutes les 30ms
      const decrement = (intervalTime / duration) * 100;
      
      const interval = setInterval(() => {
        setErrorProgress((prev) => {
          const newProgress = prev - decrement;
          return newProgress > 0 ? newProgress : 0;
        });
      }, intervalTime);
      
      return () => clearInterval(interval);
    }
  }, [showErrorMessage]);

  if (!shouldRender && !showSuccessMessage && !showErrorMessage) return null;

  const progress = Math.min((Number(elapsedTime || 0) / 30) * 100, 100);

  return (
    <>
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.15), rgba(168, 85, 247, 0.15))',
          backdropFilter: 'blur(10px)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 9999,
          opacity: isVisible ? 1 : 0,
          transition: 'opacity 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      >
        <div
          style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            padding: '50px 60px',
            borderRadius: '24px',
            textAlign: 'center',
            minWidth: '400px',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.1) inset',
            position: 'relative',
            overflow: 'hidden',
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
            transition: 'opacity 0.5s cubic-bezier(0.4, 0, 0.2, 1), transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
          }}
        >
          {/* Effet de brillance animé */}
          <div
            style={{
              position: 'absolute',
              top: '-50%',
              left: '-50%',
              width: '200%',
              height: '200%',
              background: 'linear-gradient(45deg, transparent, rgba(255, 255, 255, 0.1), transparent)',
              animation: 'shimmer 3s infinite',
              pointerEvents: 'none',
            }}
          />

          {/* Icône de loading moderne */}
          <div
            style={{
              width: '80px',
              height: '80px',
              margin: '0 auto 30px',
              position: 'relative',
            }}
          >
            <div
              style={{
                width: '100%',
                height: '100%',
                border: '4px solid rgba(255, 255, 255, 0.2)',
                borderTop: '4px solid #fff',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
              }}
            />
            <div
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                fontSize: '32px',
              }}
            >
              ✨
            </div>
          </div>

          <h2
            style={{
              color: '#fff',
              fontSize: '28px',
              fontWeight: '700',
              margin: '0 0 10px 0',
              letterSpacing: '-0.5px',
            }}
          >
            Optimisation en cours
          </h2>
          
          <p
            style={{
              color: 'rgba(255, 255, 255, 0.9)',
              fontSize: '16px',
              margin: '0 0 30px 0',
              fontWeight: '400',
            }}
          >
            Recherche de la meilleure solution...
          </p>

          {/* Barre de progression moderne */}
          <div
            style={{
              width: '100%',
              height: '8px',
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
              borderRadius: '10px',
              overflow: 'hidden',
              position: 'relative',
              boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1) inset',
            }}
          >
            <div
              style={{
                width: `${progress}%`,
                height: '100%',
                background: 'linear-gradient(90deg, #fff, #f0f0f0)',
                borderRadius: '10px',
                transition: 'width 0.1s ease-out',
                boxShadow: '0 0 10px rgba(255, 255, 255, 0.3)',
              }}
            />
          </div>

          {/* Timer stylé */}
          <div
            style={{
              marginTop: '25px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '10px',
            }}
          >
            <div
              style={{
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                padding: '8px 20px',
                borderRadius: '20px',
                backdropFilter: 'blur(10px)',
              }}
            >
              <span
                style={{
                  color: '#fff',
                  fontSize: '18px',
                  fontWeight: '600',
                  fontFamily: 'monospace',
                }}
              >
                {Number(elapsedTime || 0).toFixed(1)}s
              </span>
              <span
                style={{
                  color: 'rgba(255, 255, 255, 0.7)',
                  fontSize: '14px',
                  marginLeft: '5px',
                }}
              >
                / 30s
              </span>
            </div>
          </div>

          {/* Message de progression */}
          <p
            style={{
              color: 'rgba(255, 255, 255, 0.8)',
              fontSize: '13px',
              marginTop: '20px',
              fontStyle: 'italic',
            }}
          >
            {progress < 30 && '🚀 Analyse des contraintes...'}
            {progress >= 30 && progress < 60 && '🎯 Calcul des positions optimales...'}
            {progress >= 60 && progress < 90 && '⚡ Affinage de la solution...'}
            {progress >= 90 && '✨ Finalisation...'}
          </p>
        </div>
      </div>

      {/* Message de succès */}
      {showSuccessMessage && (
        <>
          {/* Overlay cliquable pour fermer le message */}
          <div
            onClick={() => setShowSuccessMessage(false)}
            onContextMenu={(e) => {
              e.preventDefault();
              setShowSuccessMessage(false);
            }}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100vw',
              height: '100vh',
              zIndex: 10000,
              cursor: 'default',
            }}
          />
          
          {/* Message de succès au-dessus de l'overlay */}
          <div
            style={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 10001,
              animation: 'successPop 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
              pointerEvents: 'none',
            }}
          >
            <div
              style={{
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                padding: '30px 50px',
                borderRadius: '20px',
                textAlign: 'center',
                boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
                animation: 'successFade 3s ease-in-out forwards',
                position: 'relative',
                pointerEvents: 'auto',
              }}
              onClick={(e) => e.stopPropagation()}
              onContextMenu={(e) => e.stopPropagation()}
            >
              {/* Bouton de fermeture */}
              <button
                onClick={() => setShowSuccessMessage(false)}
                style={{
                  position: 'absolute',
                  top: '10px',
                  right: '10px',
                  background: 'rgba(255, 255, 255, 0.2)',
                  border: 'none',
                  borderRadius: '50%',
                  width: '30px',
                  height: '30px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  color: '#fff',
                  fontSize: '18px',
                  fontWeight: 'bold',
                  transition: 'background 0.2s',
                }}
                onMouseEnter={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.3)'}
                onMouseLeave={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.2)'}
              >
                ×
              </button>

              <div style={{ fontSize: '48px', marginBottom: '15px' }}>✓</div>
              <h3
                style={{
                  color: '#fff',
                  fontSize: '24px',
                  fontWeight: '700',
                  margin: '0',
                }}
              >
                Optimisation réussie !
              </h3>
              <p
                style={{
                  color: 'rgba(255, 255, 255, 0.9)',
                  fontSize: '14px',
                  margin: '10px 0 0 0',
                }}
              >
                Les positions ont été optimisées
              </p>

              {/* Barre de progression */}
              <div
                style={{
                  width: '100%',
                  height: '4px',
                  backgroundColor: 'rgba(255, 255, 255, 0.3)',
                  borderRadius: '2px',
                  overflow: 'hidden',
                  marginTop: '20px',
                }}
              >
                <div
                  style={{
                    width: `${successProgress}%`,
                    height: '100%',
                    backgroundColor: '#fff',
                    borderRadius: '2px',
                    transition: 'width 0.03s linear',
                  }}
                />
              </div>
            </div>
          </div>
        </>
      )}

      {/* Message d'erreur */}
      {showErrorMessage && (
        <>
          {/* Backdrop cliquable */}
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100vw',
              height: '100vh',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              zIndex: 10000,
              animation: 'fadeIn 0.3s ease-in-out',
            }}
            onClick={() => setShowErrorMessage(false)}
          />
          
          <div
            style={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 10001,
              animation: 'successPop 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
              pointerEvents: 'none',
            }}
          >
            <div
              style={{
                background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                padding: '30px 50px',
                borderRadius: '20px',
                textAlign: 'center',
                boxShadow: '0 20px 60px rgba(239, 68, 68, 0.4)',
                animation: 'successFade 5s ease-in-out forwards',
                position: 'relative',
                pointerEvents: 'auto',
              }}
              onClick={(e) => e.stopPropagation()}
              onContextMenu={(e) => e.stopPropagation()}
            >
              {/* Bouton de fermeture */}
              <button
                onClick={() => setShowErrorMessage(false)}
                style={{
                  position: 'absolute',
                  top: '10px',
                  right: '10px',
                  background: 'rgba(255, 255, 255, 0.2)',
                  border: 'none',
                  borderRadius: '50%',
                  width: '30px',
                  height: '30px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  color: '#fff',
                  fontSize: '18px',
                  fontWeight: 'bold',
                  transition: 'background 0.2s',
                }}
                onMouseEnter={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.3)'}
                onMouseLeave={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.2)'}
              >
                ×
              </button>

              <div style={{ fontSize: '48px', marginBottom: '15px' }}>✗</div>
              <h3
                style={{
                  color: '#fff',
                  fontSize: '24px',
                  fontWeight: '700',
                  margin: '0',
                }}
              >
                Échec de l'optimisation
              </h3>
              <p
                style={{
                  color: 'rgba(255, 255, 255, 0.9)',
                  fontSize: '14px',
                  margin: '10px 0 0 0',
                  maxWidth: '500px',
                }}
              >
                {errorMessage?.message}
              </p>
              <p
                style={{
                  color: 'rgba(255, 255, 255, 0.8)',
                  fontSize: '13px',
                  margin: '15px 0 0 0',
                  maxWidth: '500px',
                  fontStyle: 'italic',
                }}
              >
                Veuillez vérifier les contraintes de votre chorégraphie (liaisons, skills, stunts)
              </p>

              {/* Barre de progression */}
              <div
                style={{
                  width: '100%',
                  height: '4px',
                  backgroundColor: 'rgba(255, 255, 255, 0.3)',
                  borderRadius: '2px',
                  overflow: 'hidden',
                  marginTop: '20px',
                }}
              >
                <div
                  style={{
                    width: `${errorProgress}%`,
                    height: '100%',
                    backgroundColor: '#fff',
                    borderRadius: '2px',
                    transition: 'width 0.03s linear',
                  }}
                />
              </div>
            </div>
          </div>
        </>
      )}

      <style>
        {`
          @keyframes fadeIn {
            from {
              opacity: 0;
            }
            to {
              opacity: 1;
            }
          }

          @keyframes slideUp {
            from {
              opacity: 0;
              transform: translateY(30px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }

          @keyframes spin {
            from {
              transform: rotate(0deg);
            }
            to {
              transform: rotate(360deg);
            }
          }

          @keyframes shimmer {
            0% {
              transform: translateX(-100%) translateY(-100%) rotate(45deg);
            }
            100% {
              transform: translateX(100%) translateY(100%) rotate(45deg);
            }
          }

          @keyframes successPop {
            0% {
              opacity: 0;
              transform: translate(-50%, -50%) scale(0.8);
            }
            50% {
              transform: translate(-50%, -50%) scale(1.05);
            }
            100% {
              opacity: 1;
              transform: translate(-50%, -50%) scale(1);
            }
          }

          @keyframes successFade {
            0%, 85% {
              opacity: 1;
            }
            100% {
              opacity: 0;
            }
          }
        `}
      </style>
    </>
  );
}
